<template>
  <div class="tp" role="group" :aria-label="t('player.panels.transcript.title')">
    <!-- Toolbar: search, language, download -->
    <div class="tp__toolbar">
      <div class="tp__search">
        <svg class="tp__search-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M9.5 3a6.5 6.5 0 0 1 5.18 10.43l5.45 5.44-1.42 1.42-5.44-5.45A6.5 6.5 0 1 1 9.5 3Zm0 2a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9Z" />
        </svg>
        <input
          v-model="query"
          type="search"
          class="tp__search-input"
          :placeholder="t('player.panels.transcript.searchPlaceholder')"
          :aria-label="t('player.panels.transcript.search')"
          @keydown.enter.prevent="$event.shiftKey ? previousMatch() : nextMatch()"
          @keydown.escape="query = ''"
        />
        <template v-if="query">
          <span class="tp__search-count" aria-hidden="true">
            {{ occurrences.length ? `${currentMatch + 1} / ${occurrences.length}` : t('player.panels.transcript.noMatch') }}
          </span>
          <button
            type="button"
            class="tp__icon-btn"
            :disabled="occurrences.length === 0"
            :aria-label="t('player.panels.transcript.previousMatch')"
            @click="previousMatch"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="m12 8-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z" /></svg>
          </button>
          <button
            type="button"
            class="tp__icon-btn"
            :disabled="occurrences.length === 0"
            :aria-label="t('player.panels.transcript.nextMatch')"
            @click="nextMatch"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z" /></svg>
          </button>
        </template>
      </div>

      <div v-if="player.availableSubtitleTracks.value.length > 1 || transcriptUrl" class="tp__toolbar-row">
        <select
          v-if="player.availableSubtitleTracks.value.length > 1"
          id="tp-language"
          class="tp__select"
          :value="transcript.selectedTrackIndex.value"
          :aria-label="t('player.panels.transcript.selectLanguage')"
          @change="handleTrackChange"
        >
          <option
            v-for="track in player.availableSubtitleTracks.value"
            :key="track.index"
            :value="track.index"
          >
            {{ track.name || track.language }}
          </option>
        </select>
        <a
          v-if="transcriptUrl"
          class="tp__download"
          :href="transcriptUrl"
          :aria-label="t('player.panels.transcript.download')"
          download
        >
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M11 4h2v8.17l3.59-3.58L18 10l-6 6-6-6 1.41-1.41L11 12.17zM5 18h14v2H5z" /></svg>
          <span class="tp__download-label">{{ t('player.panels.transcript.download') }}</span>
        </a>
      </div>

      <!-- One row however many voices there are, scrolled sideways: wrapped, a long cast pushed the
           lines out of a panel the height of the player. -->
      <ul
        v-if="voices.length > 0"
        class="tp__voices"
        :aria-label="t('player.panels.transcript.speakers')"
        @wheel="scrollVoicesSideways"
      >
        <li v-for="voice in voices" :key="voice.label" class="tp__voice">
          <span class="tp__voice-dot" :style="{ backgroundColor: voice.color }" aria-hidden="true" />
          {{ voice.name }}
        </li>
      </ul>
    </div>

    <!-- States -->
    <div v-if="transcript.isLoading.value" class="tp__placeholder">
      {{ t('player.panels.transcript.loading') }}
    </div>
    <div v-else-if="transcript.error.value" class="tp__placeholder">
      {{ t(`player.panels.transcript.${transcript.error.value}`) }}
    </div>
    <div v-else-if="player.availableSubtitleTracks.value.length === 0" class="tp__placeholder">
      {{ t('player.panels.transcript.notAvailable') }}
    </div>

    <!-- Cues, under their chapters -->
    <div
      v-else-if="transcript.cues.value.length > 0"
      ref="scrollContainer"
      class="tp__cues"
      :style="{ '--tp-text-size': `${textSize}rem` }"
      @wheel.passive="stopFollowing"
      @touchmove.passive="stopFollowing"
      @keydown="navigate"
      @focusin="rememberFocus"
    >
      <template v-for="group in groups" :key="group.key">
        <button
          v-if="group.chapter"
          type="button"
          class="tp__chapter"
          :class="{ 'tp__chapter--still': !player.canSeek.value }"
          :data-nav="`h-${group.key}`"
          :tabindex="tabStop === `h-${group.key}` ? 0 : -1"
          :aria-disabled="player.canSeek.value ? undefined : 'true'"
          @click="player.seek(group.chapter.startTime)"
        >
          <span class="tp__chapter-index">{{ group.number }}</span>
          <span class="tp__chapter-title">{{ group.chapter.title }}</span>
          <span class="tp__chapter-time">{{ formatTime(displayTime(group.chapter.startTime)) }}</span>
        </button>
        <div
          v-for="index in group.cues"
          :key="transcript.cues.value[index]!.id"
          :ref="el => setCueRef(index, el)"
          class="tp__cue"
          :class="{
            'tp__cue--active': index === transcript.activeCueIndex.value,
            'tp__cue--still': !player.canSeek.value,
          }"
          :data-nav="`c-${index}`"
          :tabindex="tabStop === `c-${index}` ? 0 : -1"
          :role="player.canSeek.value ? 'button' : undefined"
          :aria-current="index === transcript.activeCueIndex.value ? 'true' : undefined"
          @click="transcript.seekToCue(index)"
          @keydown.enter="transcript.seekToCue(index)"
          @keydown.space="seekOnSpace($event, index)"
        >
          <span class="tp__cue-time">{{ formatTime(displayTime(transcript.cues.value[index]!.start)) }}</span>
          <span class="tp__cue-body">
            <span v-if="cueVoices[index]" class="tp__cue-speaker" :style="{ color: cueVoices[index]!.color }">
              {{ cueVoices[index]!.name }}
            </span>
            <span class="tp__cue-text">
              <template v-for="(piece, p) in cuePieces[index]" :key="p">
                <mark v-if="piece.match" class="tp__mark" :class="{ 'tp__mark--current': piece.current }">{{ piece.text }}</mark>
                <template v-else>{{ piece.text }}</template>
              </template>
            </span>
          </span>
        </div>
      </template>
    </div>

    <!-- Footer: follow, link, size -->
    <div v-if="transcript.cues.value.length > 0" class="tp__footer">
      <label class="tp__follow">
        <input v-model="follow" type="checkbox" role="switch" class="tp__switch" />
        <span>{{ t('player.panels.transcript.follow') }}</span>
      </label>
      <div class="tp__footer-actions">
        <button
          v-if="linkable"
          type="button"
          class="tp__text-btn"
          :title="t('player.panels.transcript.copyLink')"
          @click="copyLink"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M10.6 13.4a1 1 0 0 1 0-1.4l3-3a1 1 0 1 1 1.4 1.4l-3 3a1 1 0 0 1-1.4 0Zm-2.3 4.9a3.5 3.5 0 0 1-2.5-6l2.1-2.1 1.4 1.4-2.1 2.1a1.5 1.5 0 1 0 2.1 2.1l2.1-2.1 1.4 1.4-2.1 2.1a3.5 3.5 0 0 1-2.4 1.1Zm8-5.1-1.4-1.4 2.1-2.1a1.5 1.5 0 0 0-2.1-2.1l-2.1 2.1-1.4-1.4 2.1-2.1a3.5 3.5 0 0 1 4.9 4.9z" /></svg>
          <span class="tp__link-label">{{ linkCopied ? t('player.panels.transcript.linkCopied') : t('player.panels.transcript.link') }}</span>
        </button>
        <span class="tp__size" role="group" :aria-label="t('player.panels.transcript.textSize')">
          <span class="tp__size-label" aria-hidden="true">{{ t('player.panels.transcript.text') }}</span>
          <button
            type="button"
            class="tp__text-btn"
            :disabled="transcriptSize === 0"
            :aria-label="t('player.panels.transcript.smaller')"
            @click="setTranscriptSize(transcriptSize - 1)"
          >A−</button>
          <button
            type="button"
            class="tp__text-btn"
            :disabled="transcriptSize === TRANSCRIPT_TEXT_SIZES.length - 1"
            :aria-label="t('player.panels.transcript.larger')"
            @click="setTranscriptSize(transcriptSize + 1)"
          >A+</button>
        </span>
      </div>
      <!-- A page that framed the player without `clipboard-write` refuses the copy: the
           address is shown, selected, to copy by hand. -->
      <input
        v-if="fallbackLink"
        class="tp__link-field"
        readonly
        :value="fallbackLink"
        :aria-label="t('player.panels.transcript.copyLink')"
        @focus="($event.target as HTMLInputElement).select()"
      />
    </div>

    <div class="tp__sr-only" aria-live="polite">{{ announcement }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUpdate, ref, watch } from 'vue'
import type { UseVideoPlayerReturn } from '../../composables/useVideoPlayer'
import { renditionOf, useTranscript, type TranscriptSpeaker } from '../../composables/useTranscript'
import { TRANSCRIPT_TEXT_SIZES, usePlayerPreferences } from '../../../common/composables/usePlayerPreferences'
import { isEmbedPage, linkToInstant } from '../../../embed/pageAddress'
import { useVideoI18n } from '../../i18n'
import { formatTime } from '../../utils/time'
import {
  findOccurrences,
  foldTexts,
  pieces,
  speakerColor,
  speakerRanks,
  type Occurrence,
  type Piece,
} from '../../utils/transcript'

interface Props {
  player: UseVideoPlayerReturn
  /** `false` on an embed asked for no chapters (`show_chapters=0`): no headings, as no markers. */
  showChapters?: boolean
}

const props = withDefaults(defineProps<Props>(), { showChapters: true })
const { t } = useVideoI18n()
const { transcriptSize, setTranscriptSize } = usePlayerPreferences()

const scrollContainer = ref<HTMLElement | null>(null)
const cueRefs = new Map<number, Element>()

function setCueRef(index: number, el: any): void {
  if (el) cueRefs.set(index, el as Element)
}

onBeforeUpdate(() => {
  cueRefs.clear()
})

const transcript = useTranscript({
  currentTime: props.player.currentTime,
  availableSubtitleTracks: props.player.availableSubtitleTracks,
  getSubtitleTrackUrl: props.player.hlsManager.getSubtitleTrackUrl,
  seek: props.player.seek,
})

// A cue is a way to its moment only while the viewer may seek; otherwise Space is the page's.
function seekOnSpace(event: KeyboardEvent, index: number): void {
  if (!props.player.canSeek.value) return
  event.preventDefault()
  transcript.seekToCue(index)
}

function handleTrackChange(event: Event): void {
  const target = event.target as HTMLSelectElement
  transcript.selectTrack(Number(target.value))
}

const textSize = computed(() => TRANSCRIPT_TEXT_SIZES[transcriptSize.value] ?? TRANSCRIPT_TEXT_SIZES[0])

// The panel speaks the control bar's clock: on a trimmed asset, the time from the trim's start.
function displayTime(time: number): number {
  return Math.max(0, time - props.player.trimStart.value)
}

// ── Voices ──
const ranks = computed(() =>
  speakerRanks(transcript.cues.value.flatMap((cue) => (cue.speaker ? [cue.speaker.label] : []))),
)

function voiceOf(speaker: TranscriptSpeaker): { name: string; color: string } {
  const rank = ranks.value.get(speaker.label) ?? 1
  return {
    name: speaker.name || t('player.panels.transcript.speaker', { n: rank }),
    color: speakerColor(speaker.color, rank),
  }
}

// Each line's voice, worked out once per cue set: the list is drawn again on every line the
// playback reaches, and this keeps that drawing to reading an array.
const cueVoices = computed(() => transcript.cues.value.map((cue) => (cue.speaker ? voiceOf(cue.speaker) : null)))

const voices = computed(() => {
  const seen = new Map<string, TranscriptSpeaker>()
  for (const cue of transcript.cues.value) {
    if (cue.speaker && !seen.has(cue.speaker.label)) seen.set(cue.speaker.label, cue.speaker)
  }
  return [...seen.values()]
    .sort((a, b) => (ranks.value.get(a.label) ?? 0) - (ranks.value.get(b.label) ?? 0))
    .map((speaker) => ({ label: speaker.label, ...voiceOf(speaker) }))
})

// A mouse wheel only scrolls vertically: on the voices' row, it scrolls the row instead. Taken from
// the page only when the row moved: at either end, the page around the player goes on scrolling.
function scrollVoicesSideways(event: WheelEvent): void {
  const row = event.currentTarget as HTMLElement
  if (event.deltaX !== 0) return
  const before = row.scrollLeft
  row.scrollLeft += event.deltaY
  if (row.scrollLeft !== before) event.preventDefault()
}

// ── Chapters: each cue under the chapter it starts in ──
interface Group {
  key: string
  number: number
  chapter: { title: string; startTime: number } | null
  cues: number[]
}

const groups = computed<Group[]>(() => {
  const cues = transcript.cues.value
  const shown = props.showChapters ? (props.player.chapters.value ?? []) : []
  const chapters = [...shown].sort((a, b) => a.startTime - b.startTime)
  if (chapters.length === 0) {
    return [{ key: 'all', number: 0, chapter: null, cues: cues.map((_, index) => index) }]
  }
  const out: Group[] = [{ key: 'before', number: 0, chapter: null, cues: [] }]
  chapters.forEach((chapter, index) => {
    out.push({ key: `chapter-${index}`, number: index + 1, chapter, cues: [] })
  })
  cues.forEach((cue, index) => {
    let at = 0
    for (let c = 0; c < chapters.length; c++) {
      if (cue.start + 0.001 >= chapters[c]!.startTime) at = c + 1
    }
    out[at]!.cues.push(index)
  })
  return out.filter((group) => group.chapter !== null || group.cues.length > 0)
})

// ── Following playback ──
const follow = ref(true)
const reducedMotion = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// The list scrolls, and only the list: `scrollIntoView` would scroll every ancestor too, the page
// the player sits in among them.
function scrollToCue(index: number): void {
  nextTick(() => {
    const el = cueRefs.get(index) as HTMLElement | undefined
    const list = scrollContainer.value
    if (!el || !list) return
    const top = el.offsetTop - list.offsetTop - (list.clientHeight - el.offsetHeight) / 2
    list.scrollTo({ top: Math.max(0, top), behavior: reducedMotion ? 'auto' : 'smooth' })
  })
}

// Scrolled just enough to show `el`, for a reader moving through the list with the keyboard.
function keepInView(el: HTMLElement): void {
  const list = scrollContainer.value
  if (!list) return
  const top = el.offsetTop - list.offsetTop
  if (top < list.scrollTop) list.scrollTop = top
  else if (top + el.offsetHeight > list.scrollTop + list.clientHeight) {
    list.scrollTop = top + el.offsetHeight - list.clientHeight
  }
}

// A reader scrolling the list is reading it: the panel stops pulling the list away.
function stopFollowing(): void {
  if (follow.value) follow.value = false
}

watch(() => transcript.activeCueIndex.value, (index) => {
  if (index !== -1 && follow.value) scrollToCue(index)
})

watch(follow, (on) => {
  const index = transcript.activeCueIndex.value
  if (on && index !== -1) scrollToCue(index)
})

// ── Keyboard: the list is one tab stop ──
// A three-hour session is thousands of lines: one tab stop each would put the footer out of reach.
// Tab enters the list on the line being played (or the one last visited), the arrows move in it.
const focusKey = ref<string | null>(null)

const tabStop = computed(() => {
  if (focusKey.value) return focusKey.value
  const active = transcript.activeCueIndex.value
  if (active !== -1) return `c-${active}`
  const first = groups.value[0]
  if (!first) return null
  return first.chapter ? `h-${first.key}` : `c-${first.cues[0]}`
})

watch(() => transcript.cues.value, () => {
  focusKey.value = null
})

function rememberFocus(event: FocusEvent): void {
  const key = (event.target as HTMLElement).dataset?.nav
  if (key) focusKey.value = key
}

const NAVIGATION_STEPS: Record<string, (at: number, last: number) => number> = {
  ArrowDown: (at, last) => Math.min(last, at + 1),
  ArrowUp: (at) => Math.max(0, at - 1),
  PageDown: (at, last) => Math.min(last, at + 10),
  PageUp: (at) => Math.max(0, at - 10),
  Home: () => 0,
  End: (_, last) => last,
}

function navigate(event: KeyboardEvent): void {
  const step = NAVIGATION_STEPS[event.key]
  const list = scrollContainer.value
  if (!step || !list || event.altKey || event.ctrlKey || event.metaKey) return
  const items = [...list.querySelectorAll<HTMLElement>('[data-nav]')]
  const at = items.indexOf(document.activeElement as HTMLElement)
  if (at === -1) return
  // The player's own shortcuts read the same keys (volume, seeking): the list keeps them.
  event.preventDefault()
  event.stopPropagation()
  const target = items[step(at, items.length - 1)]!
  stopFollowing()
  target.focus({ preventScroll: true })
  keepInView(target)
}

// ── Search ──
const query = ref('')
const currentMatch = ref(0)
const announcement = ref('')

const foldedTexts = computed(() => foldTexts(transcript.cues.value.map((cue) => cue.text)))

const occurrences = computed<Occurrence[]>(() => findOccurrences(foldedTexts.value, query.value))

const occurrencesByCue = computed(() => {
  const byCue = new Map<number, Occurrence[]>()
  for (const occurrence of occurrences.value) {
    const list = byCue.get(occurrence.cue) ?? []
    list.push(occurrence)
    byCue.set(occurrence.cue, list)
  }
  return byCue
})

// Each line cut into its pieces once per search, current match or cue set — not on every line the
// playback reaches.
const cuePieces = computed<Piece[][]>(() => {
  const current = occurrences.value[currentMatch.value] ?? null
  return transcript.cues.value.map((cue, index) => {
    const ranges = occurrencesByCue.value.get(index)
    return ranges ? pieces(cue.text, ranges, current) : [{ text: cue.text, match: false, current: false }]
  })
})

function showMatch(position: number): void {
  const total = occurrences.value.length
  if (total === 0) return
  currentMatch.value = ((position % total) + total) % total
  follow.value = false
  scrollToCue(occurrences.value[currentMatch.value]!.cue)
  announcement.value = t('player.panels.transcript.matchPosition', { current: currentMatch.value + 1, total })
}

function nextMatch(): void {
  showMatch(currentMatch.value + 1)
}

function previousMatch(): void {
  showMatch(currentMatch.value - 1)
}

// Another query, or the same one over other cues (another track, another item): the count starts
// over on the first occurrence, so it never points past the last one.
watch(occurrences, (found) => {
  currentMatch.value = 0
  if (found.length > 0) showMatch(0)
  else announcement.value = query.value.trim() ? t('player.panels.transcript.noMatch') : ''
})

// ── A link to this instant ──
// Only the embed page reads `start_at`; elsewhere — the application's own preview, a channel's
// page — the address would not seek, so nothing is offered.
const linkable = typeof window !== 'undefined' && isEmbedPage(new URL(window.location.href))
const linkCopied = ref(false)
const fallbackLink = ref<string | null>(null)
let copiedTimer: ReturnType<typeof setTimeout> | undefined

async function copyLink(): Promise<void> {
  // The entry being watched too: in a collection, a time alone would open its first entry.
  const link = linkToInstant(
    new URL(window.location.href),
    props.player.currentTime.value,
    props.player.currentIndex.value,
  )
  try {
    await navigator.clipboard.writeText(link)
    fallbackLink.value = null
    linkCopied.value = true
    announcement.value = t('player.panels.transcript.linkCopied')
    clearTimeout(copiedTimer)
    copiedTimer = setTimeout(() => {
      linkCopied.value = false
    }, 2000)
  } catch {
    fallbackLink.value = link
  }
}

// ── Download ──
const transcriptUrl = computed(() =>
  transcript.documentUrl.value ? renditionOf(transcript.documentUrl.value, 'transcript.txt') : null,
)
</script>

<style scoped lang="scss">
@use '../../styles/mixins' as *;

$voices-fade: 24px;

.tp {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-x: hidden;
  // Its own height decides the layout: a player a phone draws 220 px high leaves the panel too
  // little room for a toolbar on two rows and a footer (see the container query at the end).
  container: tp / size;

  // ── Toolbar ──
  &__toolbar {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 14px;
    flex-shrink: 0;
    border-bottom: 1px solid var(--vp-border-subtle);
  }

  &__search {
    display: flex;
    align-items: center;
    gap: 4px;
    min-height: 34px;
    padding: 0 4px 0 10px;
    border: 1px solid var(--vp-border-default);
    border-radius: 8px;
    background-color: var(--vp-surface-hover);
    transition: border-color 0.15s ease;

    &:focus-within {
      border-color: var(--vp-primary-color, #007bff);
      box-shadow: 0 0 0 1px var(--vp-primary-color, #007bff);
    }
  }

  &__search-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: var(--vp-text-tertiary);
  }

  &__search-input {
    flex: 1;
    min-width: 0;
    padding: 6px 4px;
    background: transparent;
    border: none;
    outline: none;
    color: var(--vp-text-primary);
    font-family: inherit;
    font-size: 0.8125rem;

    &::placeholder {
      color: var(--vp-text-tertiary);
    }

    &::-webkit-search-cancel-button {
      display: none;
    }
  }

  &__search-count {
    flex-shrink: 0;
    padding: 0 4px;
    font-size: 0.75rem;
    font-variant-numeric: tabular-nums;
    color: var(--vp-text-tertiary);
    white-space: nowrap;
  }

  &__icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: 6px;
    color: var(--vp-text-primary);
    cursor: pointer;

    svg {
      width: 18px;
      height: 18px;
    }

    &:hover:not(:disabled) {
      background-color: var(--vp-surface-active);
    }

    &:disabled {
      opacity: 0.4;
      cursor: default;
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: 1px;
    }
  }

  &__toolbar-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__select {
    appearance: none;
    flex: 1;
    min-width: 0;
    padding: 6px 28px 6px 10px;
    background-color: var(--vp-surface-hover);
    color: var(--vp-text-primary);
    border: 1px solid var(--vp-border-default);
    border-radius: 8px;
    font-size: 0.75rem;
    font-family: inherit;
    cursor: pointer;
    color-scheme: var(--vp-color-scheme, dark);
    background-image: var(--vp-select-arrow);
    background-repeat: no-repeat;
    background-position: right 6px center;
    background-size: 14px;

    option {
      background-color: var(--vp-select-option-bg);
      color: var(--vp-text-primary);
    }

    &:hover {
      background-color: var(--vp-surface-active);
      border-color: var(--vp-border-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: 2px;
    }
  }

  &__download {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    margin-left: auto;
    padding: 6px 12px;
    border-radius: 8px;
    background-color: var(--vp-primary-color, #7c3aed);
    color: #fff;
    font-size: 0.75rem;
    font-weight: 600;
    text-decoration: none;

    svg {
      width: 16px;
      height: 16px;
    }

    &:hover {
      filter: brightness(1.08);
    }

    &:focus-visible {
      outline: 2px solid var(--vp-text-primary);
      outline-offset: 2px;
    }
  }

  &__voices {
    display: flex;
    gap: 6px;
    margin: 0;
    padding: 0 $voices-fade 2px 0;
    list-style: none;
    overflow-x: auto;
    scrollbar-width: none;
    // The chips cut at the edge fade out, which says the row goes on; the padding as wide as the
    // fade lets the last one clear it at the end of the scroll.
    mask-image: linear-gradient(to right, #000 calc(100% - #{$voices-fade}), transparent);

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__voice {
    flex-shrink: 0;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border: 1px solid var(--vp-border-default);
    border-radius: 999px;
    font-size: 0.6875rem;
    font-weight: 600;
    color: var(--vp-text-primary);
  }

  &__voice-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
  }

  // ── States ──
  &__placeholder {
    padding: 24px 16px;
    text-align: center;
    color: var(--vp-text-tertiary);
    font-size: 0.8125rem;
    font-style: italic;
  }

  // ── Cues ──
  &__cues {
    --tp-text-size: 0.8125rem;

    overflow-y: auto;
    flex: 1;
    min-height: 0;
    padding: 6px 10px 10px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    @include custom-scrollbar;
  }

  &__chapter {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 10px 0 4px;
    padding: 4px 10px;
    background: transparent;
    border: none;
    border-radius: 6px;
    color: var(--vp-text-tertiary);
    font-family: inherit;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-align: left;
    text-transform: uppercase;
    cursor: pointer;

    &:first-child {
      margin-top: 2px;
    }

    &:hover {
      color: var(--vp-text-primary);
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: 1px;
    }

    // A heading to read, not a way in: the viewer may not move the playhead (`seeking`).
    &--still {
      cursor: default;
    }

    &--still:hover {
      color: var(--vp-text-tertiary);
    }
  }

  &__chapter-index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    border-radius: 4px;
    background: var(--vp-surface-hover);
    font-variant-numeric: tabular-nums;
  }

  &__chapter-title {
    flex: 1;
    min-width: 0;
    @include truncate-text;
  }

  &__chapter-time {
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }

  &__cue {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 10px;
    cursor: pointer;
    border-radius: 8px;
    border-left: 3px solid transparent;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: var(--vp-surface-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: 2px;
    }

    // Read, not followed: the viewer may not move the playhead (`seeking`).
    &--still {
      cursor: default;
    }

    &--still:not(.tp__cue--active):hover {
      background-color: transparent;
    }

    &--active {
      background-color: var(--vp-surface-hover);
      border-left-color: var(--vp-primary-color, #7c3aed);

      .tp__cue-time {
        color: #fff;
        background: var(--vp-primary-color, #7c3aed);
        border-color: transparent;
      }

      .tp__cue-text {
        color: var(--vp-text-primary);
      }
    }
  }

  &__cue-time {
    font-size: 0.75rem;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: var(--vp-text-tertiary);
    min-width: 44px;
    flex-shrink: 0;
    padding: 3px 6px;
    background: var(--vp-surface-subtle);
    border: 1px solid var(--vp-border-subtle);
    border-radius: 4px;
    text-align: center;
    transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  }

  &__cue-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    padding-top: 1px;
  }

  &__cue-speaker {
    font-size: 0.6875rem;
    font-weight: 700;
  }

  &__cue-text {
    font-size: var(--tp-text-size);
    color: var(--vp-text-tertiary);
    line-height: 1.5;
    word-break: break-word;
    transition: color 0.15s ease;
  }

  &__mark {
    padding: 0 2px;
    border-radius: 3px;
    background: rgba(250, 204, 21, 0.45);
    color: inherit;

    &--current {
      background: #facc15;
      color: #111;
    }
  }

  // ── Footer ──
  &__footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 14px;
    flex-shrink: 0;
    border-top: 1px solid var(--vp-border-subtle);
    font-size: 0.75rem;
    color: var(--vp-text-primary);
  }

  &__follow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }

  &__switch {
    appearance: none;
    position: relative;
    width: 30px;
    height: 17px;
    margin: 0;
    border-radius: 999px;
    background: var(--vp-surface-active);
    cursor: pointer;
    transition: background-color 0.15s ease;

    &::after {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 13px;
      height: 13px;
      border-radius: 50%;
      background: #fff;
      transition: transform 0.15s ease;
    }

    &:checked {
      background: var(--vp-primary-color, #7c3aed);

      &::after {
        transform: translateX(13px);
      }
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: 2px;
    }
  }

  &__footer-actions {
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }

  &__size {
    display: inline-flex;
    align-items: center;
    gap: 2px;
  }

  &__size-label {
    margin-right: 4px;
    color: var(--vp-text-tertiary);
  }

  &__text-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 28px;
    height: 26px;
    padding: 0 6px;
    background: transparent;
    border: 1px solid var(--vp-border-subtle);
    border-radius: 6px;
    color: inherit;
    font-family: inherit;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;

    svg {
      width: 14px;
      height: 14px;
    }

    &:hover:not(:disabled) {
      background-color: var(--vp-surface-hover);
    }

    &:disabled {
      opacity: 0.4;
      cursor: default;
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: 1px;
    }
  }

  &__link-field {
    flex-basis: 100%;
    padding: 5px 8px;
    border: 1px solid var(--vp-border-default);
    border-radius: 6px;
    background: var(--vp-surface-hover);
    color: var(--vp-text-primary);
    font-family: inherit;
    font-size: 0.75rem;
  }

  &__sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
}

// ── Compact: a short panel keeps its lines readable ──
// One toolbar row — search, language, the download as an icon —, no voice chips (each line still
// names its voice), a tighter footer.
@container tp (max-height: 340px) {
  .tp__toolbar {
    flex-direction: row;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
  }

  .tp__search {
    flex: 1;
    min-width: 0;
    min-height: 30px;
  }

  .tp__toolbar-row {
    flex: none;
    gap: 6px;
  }

  .tp__select {
    flex: none;
    max-width: 110px;
  }

  .tp__download {
    padding: 6px 8px;
  }

  .tp__download-label,
  .tp__voices,
  .tp__size-label,
  .tp__link-label {
    display: none;
  }

  .tp__cues {
    padding-top: 2px;
  }

  .tp__cue {
    padding: 5px 8px;
  }

  .tp__footer {
    padding: 4px 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tp__cue,
  .tp__cue-time,
  .tp__cue-text,
  .tp__switch,
  .tp__switch::after {
    transition: none;
  }
}
</style>
